/**
 * 系统配置 · 模型分类（design §8）。
 *
 * 值形如 {一级分类: {label, types: [二级分类]}}。一级分类固定为 perception / prediction / planning / control，
 * 只能改其下的二级分类；仍有模型类别在用的二级分类后端会拒绝删除，错误原样展示在保存栏。
 */
import { useState } from 'react';
import { App, Button, Input, Popconfirm, Space, Tag, Typography } from 'antd';
import { PlusOutlined, RollbackOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { getModelCategories, resetModelCategories, updateModelCategories } from '@apis/modelSync';
import { categoriesError, categoriesToJson, errorText, parseCategories, type CategoryRow } from '@/utils/modelSync';
import { Panel, PanelError, PanelLoading, RefreshButton, SOURCE_PILL, useConfigResource, useReportDirty } from './shared';

const { Text } = Typography;
const CARD_ID = 'sec-model-categories';

export default function ModelCategoriesCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadDraft = (raw: string) => {
    setSaveError(null);
    setInputs({});
    try {
      setRows(parseCategories(raw));
      setLoadError(null);
    } catch (e) {
      setRows([]);
      setLoadError(`当前配置不是合法 JSON：${(e as Error).message}，可恢复默认后再编辑`);
    }
  };
  const { loading, config, applyConfig, fetchConfig } = useConfigResource({
    id: CARD_ID, title: '模型分类', fetcher: getModelCategories, onLoaded: (cfg) => loadDraft(cfg.currentValue),
  });

  const draft = categoriesToJson(rows);
  const error = loadError ?? categoriesError(rows);
  const dirty = config !== null && !loadError && (() => {
    try { return JSON.stringify(JSON.parse(draft)) !== JSON.stringify(parseCategoriesSafe(config.currentValue)); }
    catch { return true; }
  })();
  useReportDirty(CARD_ID, dirty);

  const addType = (key: string) => {
    const t = (inputs[key] ?? '').trim();
    if (!t) return;
    setRows((rs) => rs.map((r) => (r.key === key && !r.types.includes(t) ? { ...r, types: [...r.types, t] } : r)));
    setInputs((m) => ({ ...m, [key]: '' }));
  };
  const removeType = (key: string, t: string) =>
    setRows((rs) => rs.map((r) => {
      if (r.key !== key) return r;
      const { [t]: _dropped, ...typeLabels } = r.typeLabels;   // 删二级分类时中文名一并去掉
      return { ...r, types: r.types.filter((x) => x !== t), typeLabels };
    }));
  const setTypeLabel = (key: string, t: string, label: string) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, typeLabels: { ...r.typeLabels, [t]: label } } : r)));

  const handleSave = async () => {
    if (error || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      applyConfig(await updateModelCategories(draft));
      message.success('模型分类已保存');
    } catch (e) {
      setSaveError(errorText(e, '保存失败，请重试'));
    } finally {
      setSaving(false);
    }
  };
  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetModelCategories());
      message.success('已恢复默认模型分类');
    } catch (e) {
      // 请求层已弹出错误提示
      console.error('恢复默认模型分类失败', e);
    } finally {
      setResetting(false);
    }
  };

  const busy = saving || resetting || loading;

  return (
    <Panel
      id={CARD_ID} index={index} variant="cfg" kicker="MODEL" title="模型分类" dirty={dirty}
      subtitle="一级分类固定（感知 / 预测 / 规划 / 控制），可增删其下的二级分类；登记模型类别时从这里选。"
      info={<div>二级分类不能在不同一级分类下重复；已有模型类别在用的二级分类不能删除（保存时会提示具体是哪些）。中文名可选，与中台页面上的模型类型名一致（如 LANE = 视觉车道线检测），只用于展示。</div>}
      tools={<RefreshButton loading={loading} onClick={fetchConfig} />}
    >
      {loading && !config ? <PanelLoading /> : !config ? <PanelError onRetry={fetchConfig} /> : (
        <>
          <div className="sc-meta">{SOURCE_PILL[config.source]}</div>
          <Space direction="vertical" style={{ width: '100%' }}>
            {rows.map((r) => (
              <div key={r.key}>
                <Text strong>{r.label}</Text><Text type="secondary" style={{ marginLeft: 6 }}>{r.key}</Text>
                <div style={{ marginTop: 4 }}>
                  {r.types.length === 0 && <Text type="secondary" style={{ marginRight: 8 }}>暂无二级分类</Text>}
                  {r.types.map((t) => (
                    <Space.Compact key={t} size="small" style={{ marginRight: 8, marginBottom: 4 }}>
                      <Tag closable={!busy} style={{ marginInlineEnd: 0 }} onClose={(e) => { e.preventDefault(); removeType(r.key, t); }}>{t}</Tag>
                      <Input size="small" style={{ width: 150 }} maxLength={64} placeholder="中文名（可选）" disabled={busy}
                        aria-label={`${t} 的中文名`} value={r.typeLabels[t] ?? ''} onChange={(e) => setTypeLabel(r.key, t, e.target.value)} />
                    </Space.Compact>
                  ))}
                  <Space.Compact size="small">
                    <Input size="small" style={{ width: 160 }} maxLength={64} placeholder="新二级分类" disabled={busy}
                      value={inputs[r.key] ?? ''} onChange={(e) => setInputs((m) => ({ ...m, [r.key]: e.target.value }))}
                      onPressEnter={() => addType(r.key)} />
                    <Button size="small" icon={<PlusOutlined />} disabled={busy || !(inputs[r.key] ?? '').trim()}
                      onClick={() => addType(r.key)}>添加</Button>
                  </Space.Compact>
                </div>
              </div>
            ))}
          </Space>
          <div className="sc-editor">
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirty || !!error || resetting || loading} onClick={() => void handleSave()}>保存</Button>
            <Button size="small" icon={<UndoOutlined />} title="撤销本地改动" aria-label="撤销模型分类改动" disabled={!dirty || saving || resetting}
              onClick={() => loadDraft(config.currentValue)} />
            <Popconfirm title="恢复默认模型分类？" description="二级分类恢复为出厂默认；在用的二级分类若被去掉，后续登记会受影响。" okText="恢复默认" cancelText="取消" onConfirm={handleReset}>
              <Button size="small" danger icon={<RollbackOutlined />} loading={resetting} disabled={saving || loading}>恢复默认</Button>
            </Popconfirm>
            {(error || saveError) && <span className="sc-editor-hint bad" role="alert">{error ?? saveError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}

/** 与 draft 同口径序列化后比较，避免键顺序 / 空白差异误报「有改动」。 */
function parseCategoriesSafe(raw: string): unknown {
  return JSON.parse(categoriesToJson(parseCategories(raw)));
}
